import { useCallback, useEffect, useRef, useState } from 'react';

import { api } from '../../services/api';
import { friendlyApiError } from '../../services/errorMessages';
import {
  DOWNLOAD_POLL_MS, DOWNLOAD_WAIT_MS, STILL_PREPARING_MESSAGE,
  classifyDownload, isDownloadEndpointMissing, markDownloadEndpointMissing, openRecordingDownload,
} from './recordingDownload';

/** Что скачиваем: запись комнаты или эфира старой модели. */
export interface DownloadTarget {
  source: 'room' | 'live';
  id: number;
  audioOnly: boolean;
}

export interface DownloadNotice {
  tone: 'success' | 'error';
  title: string;
  message: string;
}

const key = (t: DownloadTarget) => `${t.source}:${t.id}`;

/**
 * CORE-2046 — шаги «Скачать запись»: подтверждение с напоминанием о согласии
 * участников → запрос → (пока файл собирается) повтор каждые 5 с → файл.
 * Одна загрузка за раз: пока готовится одна, пункт у неё «Готовим файл…».
 */
export function useRecordingDownload(onNotice: (n: DownloadNotice) => void) {
  const [confirmFor, setConfirmFor] = useState<DownloadTarget | null>(null);
  const [preparingKey, setPreparingKey] = useState<string | null>(null);
  const [hidden, setHidden] = useState(isDownloadEndpointMissing());
  const alive = useRef(true);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const noticeRef = useRef(onNotice);
  noticeRef.current = onNotice;

  useEffect(() => () => {
    alive.current = false;
    if (timer.current) clearTimeout(timer.current);
  }, []);

  const run = useCallback(async (target: DownloadTarget) => {
    const started = Date.now();
    setPreparingKey(key(target));
    const step = async (): Promise<void> => {
      let outcome;
      try {
        outcome = classifyDownload(await api.requestRecordingDownload(target.source, target.id));
      } catch (error) {
        outcome = classifyDownload(null, error);
      }
      if (!alive.current) return;
      switch (outcome.kind) {
        case 'preparing':
          if (Date.now() - started >= DOWNLOAD_WAIT_MS) {
            setPreparingKey(null);
            noticeRef.current({ tone: 'success', title: 'Файл готовится', message: STILL_PREPARING_MESSAGE });
            return;
          }
          timer.current = setTimeout(() => { void step(); }, DOWNLOAD_POLL_MS);
          return;
        case 'ready':
          setPreparingKey(null);
          openRecordingDownload(outcome.url);
          return;
        case 'missing':
          // Бэкенд без этой ручки — пункт просто исчезает.
          markDownloadEndpointMissing();
          setHidden(true);
          setPreparingKey(null);
          noticeRef.current({ tone: 'error', title: 'Скачивание пока недоступно', message: 'Попробуйте позже.' });
          return;
        case 'unavailable':
          setPreparingKey(null);
          noticeRef.current({ tone: 'error', title: 'Не удалось скачать запись', message: outcome.message });
          return;
        default:
          setPreparingKey(null);
          noticeRef.current({ tone: 'error', title: 'Не удалось скачать запись', message: friendlyApiError(outcome.error) });
      }
    };
    await step();
  }, []);

  return {
    hidden,
    confirmFor,
    preparingKey,
    isPreparing: (t: Pick<DownloadTarget, 'source' | 'id'>) => preparingKey === `${t.source}:${t.id}`,
    ask: (t: DownloadTarget) => setConfirmFor(t),
    cancel: () => setConfirmFor(null),
    confirm: () => {
      const t = confirmFor;
      setConfirmFor(null);
      if (t && !preparingKey) void run(t);
    },
  };
}
